import { useEffect, useMemo, useState } from "react";

import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";

import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import LoadingWheel from "../../components/shared/LoadingWheel";
import useNotification from "../../components/notifications/useNotifications";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import { useScannerOverride } from "../../contexts/ScannerContext";
import usePartAssembly from "../../data/usePartAssembly";
import useApiHelper from "../../hooks/useApiHelper";
import { IAllJobsResponse, IJob } from "../../models/Job";
import { OmniSearchResponse, OmniSearchType } from "../../models/search/OmniSearch";
import { CheckFeatureFlag } from "../../hooks/useFeatureFlag";
import JobWorkspace from "../Jobs/JobWorkspace";
import Triage from "../Triage/Triage";
import Depollution from "../Depollution/Depollution";

// Persist the chosen station per device so a fixed floor terminal stays on its
// assembly between visits.
const STATION_STORAGE_KEY = "stationManagerAssemblyId";

// Sentinel station ids for the non-assembly modules. Selecting one shows that
// module for the scanned vehicle instead of a dismantling job.
const TRIAGE_STATION = "__triage__";
const DEPOLLUTION_STATION = "__depollution__";
const isModuleStation = (id: string) => id === TRIAGE_STATION || id === DEPOLLUTION_STATION;

const readStoredStation = (): string => {
  try {
    return window.localStorage.getItem(STATION_STORAGE_KEY) ?? "";
  } catch {
    return "";
  }
};

// From all jobs for a vehicle, prefer an in-progress (not completed) one,
// otherwise the most recently created.
const pickActiveJob = (jobs: IJob[]): IJob | undefined => {
  if (jobs.length === 0) return undefined;
  const byNewest = [...jobs].sort(
    (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime()
  );
  return byNewest.find((job) => !job.completedOn) ?? byNewest[0];
};

export default function StationManager() {
  const { setInfo } = usePageNameContext();
  const { get } = useApiHelper();
  const { addNotification } = useNotification();
  const { assemblies } = usePartAssembly();

  const hasTriage = CheckFeatureFlag("Triage");

  const [stationId, setStationId] = useState<string>(readStoredStation);
  const [reg, setReg] = useState<string>("");
  // The currently selected vehicle, kept across station changes. loadedJobId is
  // its resolved dismantling job (assembly stations only) and is always either
  // null or the job for selectedVehicleId.
  const [selectedVehicleId, setSelectedVehicleId] = useState<string | null>(null);
  const [loadedJobId, setLoadedJobId] = useState<string | null>(null);
  const [isResolving, setIsResolving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // True when the next loaded job should auto-start its timer and scroll down
  // (set when the user re-scans a different vehicle to switch to it).
  const [autoStartTimer, setAutoStartTimer] = useState(false);

  const moduleStation = isModuleStation(stationId);
  // Whether content is currently loaded for the chosen station.
  const hasLoaded = moduleStation ? !!selectedVehicleId : !!loadedJobId;

  useEffect(() => {
    setInfo({
      name: "Station Manager",
      desc: "Scan a vehicle to view its dismantling job by assembly",
    });
  }, [setInfo]);

  // Persist the station selection.
  useEffect(() => {
    try {
      if (stationId) window.localStorage.setItem(STATION_STORAGE_KEY, stationId);
      else window.localStorage.removeItem(STATION_STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }, [stationId]);

  // Assemblies in display order, matching the rest of the app.
  const stationOptions = useMemo(() => {
    const list = assemblies.data?.data ?? [];
    return [...list].sort((a, b) => {
      const orderA = a.order ?? Number.MAX_SAFE_INTEGER;
      const orderB = b.order ?? Number.MAX_SAFE_INTEGER;
      if (orderA !== orderB) return orderA - orderB;
      return a.name.localeCompare(b.name);
    });
  }, [assemblies.data]);

  // If a previously stored station no longer exists, clear it. The module
  // sentinels (Triage / Depollution) are always valid.
  useEffect(() => {
    if (assemblies.isLoading) return;
    if (stationId && !isModuleStation(stationId) && !stationOptions.some((a) => a.id === stationId)) {
      setStationId("");
    }
  }, [assemblies.isLoading, stationOptions, stationId]);

  // Resolve a vehicle id to its active job and load it into the workspace.
  // When autoStart is true the loaded job will start its timer and scroll down.
  const loadJobForVehicleId = async (vehicleId: string, autoStart = false) => {
    setIsResolving(true);
    setError(null);
    setSelectedVehicleId(vehicleId);
    try {
      const res = await get<IAllJobsResponse>(`/job/vehicle/${vehicleId}`);
      const job = pickActiveJob(res.data ?? []);
      if (!job) {
        setLoadedJobId(null);
        setError("No dismantling job found for that vehicle.");
        return;
      }
      setAutoStartTimer(autoStart);
      setLoadedJobId(job.id);
    } catch {
      setError("Couldn't load the job for that vehicle.");
    } finally {
      setIsResolving(false);
    }
  };

  // Select a vehicle for the current station: a module station just needs the
  // vehicle, an assembly station also resolves its dismantling job.
  const selectVehicle = (vehicleId: string) => {
    if (moduleStation) {
      setError(null);
      // Drop a stale job from a different vehicle so an assembly station picked
      // later re-resolves it.
      if (vehicleId !== selectedVehicleId) setLoadedJobId(null);
      setSelectedVehicleId(vehicleId);
    } else {
      loadJobForVehicleId(vehicleId);
    }
  };

  // A different vehicle was scanned inside the loaded job. Open it in this same
  // station, optionally starting the timer and scrolling down (re-scan path).
  const handleSwitchVehicle = (vehicleId: string, vrm: string | undefined, startTimer: boolean) => {
    if (vrm) setReg(vrm);
    loadJobForVehicleId(vehicleId, startTimer);
  };

  // Resolve a typed reg to a vehicle, then to its job.
  const handleLoad = async () => {
    const term = reg.trim();
    if (!stationId) {
      addNotification({ variant: "warning", primaryText: "Pick a station first" });
      return;
    }
    if (!term) return;

    setIsResolving(true);
    setError(null);
    try {
      const res = await get<OmniSearchResponse>(
        `/omnisearch?query=${encodeURIComponent(term)}&type=${OmniSearchType.Vehicle}`
      );
      const vehicle = (res.data ?? []).find((r) => r.type === OmniSearchType.Vehicle) ?? res.data?.[0];
      if (!vehicle) {
        setError(`No vehicle found for "${term}".`);
        setIsResolving(false);
        return;
      }
      if (moduleStation) {
        selectVehicle(vehicle.id);
        setIsResolving(false);
      } else {
        await loadJobForVehicleId(vehicle.id);
      }
    } catch {
      setError(`Couldn't find a vehicle for "${term}".`);
      setIsResolving(false);
    }
  };

  // Scanning a vehicle barcode ("v|{vehicleId}|{vrm}") loads it directly. For an
  // assembly station this resolves the dismantling job; for a module station it
  // just opens the module for that vehicle. Once a job is loaded, JobWorkspace
  // registers its own scanner override (most-recent-wins), so this only fires on
  // the picker screen / in module mode.
  useScannerOverride((scan) => {
    const { parts } = scan;
    if (parts[0]?.toLowerCase() !== "v" || !parts[1]) return;

    if (!stationId) {
      addNotification({ variant: "warning", primaryText: "Pick a station first" });
      return;
    }
    if (parts[2]) setReg(parts[2]);
    selectVehicle(parts[1]);
  });

  // Switching the chosen station keeps the selected vehicle and re-resolves the
  // content for the new station (an assembly station needs its job).
  const handleStationChange = (value: string) => {
    setStationId(value);
    setError(null);
    setAutoStartTimer(false);
    if (selectedVehicleId && !isModuleStation(value) && !loadedJobId) {
      loadJobForVehicleId(selectedVehicleId);
    }
  };

  const handleChangeVehicle = () => {
    setSelectedVehicleId(null);
    setLoadedJobId(null);
    setReg("");
    setError(null);
    setAutoStartTimer(false);
  };

  return (
    <div className="mt-4 space-y-4">
      {/* Picker bar */}
      <div className="rounded-lg bg-white p-4 shadow">
        <div className="flex flex-col gap-4 md:flex-row md:items-end">
          <div className="md:w-64">
            <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
              Your station
            </label>
            <select
              value={stationId}
              onChange={(e) => handleStationChange(e.target.value)}
              disabled={assemblies.isLoading}
              className="mt-1 w-full rounded-md border-gray-300 text-sm focus:border-primary-500 focus:ring-primary-500"
            >
              <option value="">{assemblies.isLoading ? "Loading…" : "Select a station…"}</option>
              <optgroup label="Stations">
                {stationOptions.map((assembly) => (
                  <option key={assembly.id} value={assembly.id}>
                    {assembly.name}
                  </option>
                ))}
              </optgroup>
              <optgroup label="Other">
                {hasTriage && <option value={TRIAGE_STATION}>Triage</option>}
                <option value={DEPOLLUTION_STATION}>Depollution</option>
              </optgroup>
            </select>
          </div>

          <div className="flex-1">
            <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
              Scan or enter vehicle reg
            </label>
            <div className="relative mt-1">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
              </div>
              <input
                type="text"
                value={reg}
                disabled={hasLoaded}
                onChange={(e) => setReg(e.target.value.toUpperCase())}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleLoad();
                }}
                placeholder="e.g. KT68XVX"
                className="w-full rounded-md border-gray-300 pl-10 text-sm uppercase focus:border-primary-500 focus:ring-primary-500 disabled:bg-gray-100"
              />
            </div>
          </div>

          <div className="flex items-center gap-x-2">
            {hasLoaded ? (
              <ButtonInput label="Change vehicle" isSubmit={false} onClick={handleChangeVehicle} classes="" />
            ) : (
              <ButtonInput
                label={moduleStation ? "Load vehicle" : "Load job"}
                isSubmit={false}
                onClick={handleLoad}
                isLoading={isResolving}
                disabled={!stationId || !reg.trim()}
                classes=""
              />
            )}
          </div>
        </div>

        {error && <p className="mt-3 text-sm font-medium text-red-600">{error}</p>}
      </div>

      {/* Content for the selected station: a dismantling job for an assembly
          station, or the Triage / Depollution module for a module station. */}
      {moduleStation && selectedVehicleId ? (
        stationId === TRIAGE_STATION ? (
          <Triage key={selectedVehicleId} vehicleId={selectedVehicleId} />
        ) : (
          <Depollution key={selectedVehicleId} vehicleId={selectedVehicleId} />
        )
      ) : !moduleStation && loadedJobId ? (
        <JobWorkspace
          key={loadedJobId}
          jobId={loadedJobId}
          restrictToAssemblyId={stationId || undefined}
          managePageTitle={false}
          onSwitchVehicle={handleSwitchVehicle}
          autoStartTimer={autoStartTimer}
          hideTriage
          collapseDismantlingTimer
        />
      ) : isResolving ? (
        <LoadingWheel />
      ) : (
        <div className="rounded-lg bg-white p-8 text-center text-gray-500 shadow">
          {!stationId
            ? "Select your station, then scan or enter a vehicle reg."
            : moduleStation
              ? "Scan or enter a vehicle reg to load it."
              : "Scan or enter a vehicle reg to load its dismantling job."}
        </div>
      )}
    </div>
  );
}
